<script setup lang="ts">
/**
 * Toast — single notification card.  Rendered inside <ToastContainer />
 * (or anywhere) and emits `dismiss` to remove itself from the queue.
 */
import { computed } from 'vue'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

interface Props {
  variant: ToastVariant
  title?: string
  description?: string
  /** Auto-dismiss after this many ms. 0 disables auto-dismiss. */
  duration?: number
}

const props = withDefaults(defineProps<Props>(), {
  duration: 0,
})

const emit = defineEmits<{
  dismiss: []
}>()

const iconMap: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

const variantClass: Record<ToastVariant, string> = {
  success: 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-950/40 dark:text-success-300',
  error:   'border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-800 dark:bg-danger-950/40 dark:text-danger-300',
  warning: 'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-800 dark:bg-warning-950/40 dark:text-warning-300',
  info:    'border-info-200 bg-info-50 text-info-700 dark:border-info-800 dark:bg-info-950/40 dark:text-info-300',
}

const show = computed(() => Boolean(props.title || props.description))
void show
</script>

<template>
  <div
    v-if="title || description"
    class="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border bg-bg-surface px-4 py-3 shadow-lg"
    :class="variantClass"
    role="alert"
  >
    <component :is="iconMap[variant]" :size="18" class="mt-0.5 shrink-0" />
    <div class="flex-1 min-w-0">
      <p v-if="title" class="text-sm font-semibold text-fg">{{ title }}</p>
      <p
        v-if="description"
        class="text-sm text-fg-muted break-words whitespace-pre-line"
      >
        {{ description }}
      </p>
    </div>
    <button
      type="button"
      class="shrink-0 rounded p-0.5 text-fg-subtle hover:bg-bg-hover hover:text-fg"
      aria-label="Dismiss"
      @click="emit('dismiss')"
    >
      <X :size="14" />
    </button>
  </div>
</template>
